import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import useRefreshToken from "../../hooks/useRefreshToken";
import useAuth from "../../hooks/useAuth";
import React from "react";
// import sidebar and headers
import Sidebar from "../../components/Sidebar";
import Header from "../../pages/Agent/Dashboard2/Header";
import useAgentHeader from "../../pages/Agent/Dashboard2/useAgentHeader";
import "../../pages/Agent/Dashboard2/Dashboard2.css";
import Call from "../Call/Call";
import FeedBack from "../CreateCampaign/FeedBack";
import WebRtcIncomingCallHandler from "../Call/webrtcCall";
import FollowupCallToast from "../Call/FollowupCallToast";

import { WebsocketProvider } from "../../context/WebsocketProvider";
import { FollowupNotificationProvider } from "../../context/FollowupNotificationContext";
import BreakReasonPopup from "../../Popups/BreakReasons";
import Adminroutes, { getRoutesByAuth } from "../../routes";

const PersistLogin = forwardRef((props, ref) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const refresh = useRefreshToken();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const isFullBleedPage = /\/agent\/dashboard\/?$/.test(location.pathname);
  const [sidebaropen, setSidebarOpen] = useState(false);
  const [currsidemenu, setCurrsidemenu] = useState(() => {
    // Read the value from local storage on initial load
    return localStorage.getItem("currsidemenu") || "Dashboard";
  });
  const [showModal, setShowModal] = useState(true);
  const [check, setcheck] = useState();
  const openSidebar = () => {
    setSidebarOpen(!sidebaropen);
  };
  const [onlineStatus, setOnlineStatus] = useState(
    localStorage.getItem("onlineStatus") || "initial_status"
  );


  useImperativeHandle(ref, () => ({
    getOnlineStatus: () => onlineStatus,
    setOnlineStatus: (status) => setOnlineStatus(status),
    setOnlineStatusColour: (status) => setOnlineStatusColour(status),
  }));

  const [onlineStatusColour, setOnlineStatusColour] = useState("");
  const [showBreakPopup, setShowBreakPopup] = useState(false);

  useEffect(() => {
    const verifyRefreshToken = async () => {
      try {
        await refresh();
      } catch (err) {
        navigate("/login");
      } finally {
        setIsLoading(false);
      }
    };
    // ON refresh we tring to get new acces token using refresh cookie token
    // this is to keep the user not go login in every rfresh
    !auth?.accessToken ? verifyRefreshToken() : setIsLoading(false);
  }, []);

  useEffect(() => {
    // Keep sidebar/current menu in sync with route for specific pages
    if (location?.pathname && location.pathname.includes("vsip-report")) {
      setCurrsidemenu("Vsip Report");
    }
  }, [location?.pathname]);



  useEffect(() => {
    // Update localStorage whenever currsidemenu changes
    localStorage.setItem("currsidemenu", currsidemenu);
  }, [currsidemenu]);

  useEffect(() => {
    const allRoutes = [...Adminroutes, ...getRoutesByAuth(auth)];

    const matchedRoute = allRoutes.find((route) => {
      // Handle dynamic routes like "campaigns/:type/"
      if (route.path.includes(":")) {
        const routeRegex = route.path.replace(/:\w+/g, "[^/]+") + "$";
        return new RegExp(routeRegex).test(
          location.pathname.replace(/^\/(admin|agent)\//, "")
        );
      }

      // Handle normal routes
      return location.pathname.endsWith(route.path);
    });

    if (matchedRoute) {
      setCurrsidemenu(matchedRoute.name);
    }
  }, [auth, location.pathname]);

  return (
    <>
      {isLoading ? (
        <p>IS LOADING...............</p>
      ) : (
        <WebsocketProvider>
          <FollowupNotificationProvider onlineStatus={onlineStatus}>
            <PersistLoginContent
              sidebaropen={sidebaropen}
              currsidemenu={currsidemenu}
              setCurrsidemenu={setCurrsidemenu}
              isFullBleedPage={isFullBleedPage}
              onlineStatus={onlineStatus}
              setOnlineStatus={setOnlineStatus}
              onlineStatusColour={onlineStatusColour}
              setOnlineStatusColour={setOnlineStatusColour}
              auth={auth}
            />
          </FollowupNotificationProvider>
        </WebsocketProvider>
      )}
    </>
  );
});

// Rendered as a child of WebsocketProvider so the header (via useAgentHeader)
// can read live call state — it stays mounted here, above <Outlet>, so it
// never remounts or resets as the user navigates between pages.
const PersistLoginContent = ({
  sidebaropen,
  currsidemenu,
  setCurrsidemenu,
  isFullBleedPage,
  onlineStatus,
  setOnlineStatus,
  onlineStatusColour,
  setOnlineStatusColour,
  auth,
}) => {
  const headerState = useAgentHeader({
    onlineStatus,
    setOnlineStatus,
    onlineStatusColour,
    setOnlineStatusColour,
  });

  // sipDetails, setSipDetails, and formConfig now live inside useAgentHeader
  // (the old components/Header.js forwardRef is no longer used). Read them
  // directly from the hook's return value so Call.js always gets live values.
  const getSipDetailsFromHeader = () => {
    return headerState.sipPopup.sipDetails;
  };

  const setSipDetailsOnHeader = (details) => {
    headerState.sipPopup.setSipDetails(details);
  };

  const formConfig = () => {
    return headerState.sipPopup.formConfig;
  }

  return (
    <div className="flex h-screen antialiased text-gray-900 bg-primarybg">
      <Sidebar
        sidebaropen={sidebaropen}
        setCurrsidemenu={setCurrsidemenu}
        currsidemenu={currsidemenu}
      />
      <div className="flex-1 min-w-0 h-full overflow-x-hidden overflow-y-auto no-scrollbar">
        <div className="dash2">
          <Header {...headerState} />
        </div>
        <WebRtcIncomingCallHandler formConfig={headerState.sipPopup.formConfig} />
        <FollowupCallToast />
        <main
          className={isFullBleedPage ? "" : "p-4 mt-4"}
          onClick={headerState.closeAllPopovers}
        >
          <Outlet
            context={{
              onlineStatus,
              setOnlineStatus,
              onlineStatusColour,
              setOnlineStatusColour,
            }}
          />
          {auth?.roles?.includes("sys_user") ? (
            <Call
              onlineStatus={onlineStatus}
              setOnlineStatus={setOnlineStatus}
              onlineStatusColour={onlineStatusColour}
              setOnlineStatusColour={setOnlineStatusColour}
              getSipDetails={getSipDetailsFromHeader} 
              setSipDetails={setSipDetailsOnHeader} 
              formConfig={formConfig}
            />
          ) : (
            <></>
          )}
        </main>
      </div>
    </div>
  );
};

export default PersistLogin;
